import type { Area, Person } from '../types';
import { agentsService } from './agentsService';
import { flowsService } from './flowsService';
import { api, ApiError } from './apiClient';

export interface AreaUsage {
  budget: number;
  allocated: number; // Σ budgets das pessoas da área
  remaining: number;
}

export interface PersonUsage {
  budget: number;
  allocated: number; // Σ tetos de agentes/fluxos vinculados à pessoa
  remaining: number;
}

// Mantida para compatibilidade com as páginas (que tratam erros de regra de
// negócio com `instanceof BudgetError`). As validações reais ocorrem no backend;
// aqui apenas reembrulhamos os erros 400 da API.
export class BudgetError extends Error {}

export interface GrantRequest {
  id: string;
  personId: string;
  areaId: string;
  amountUsd: number;
  consumedUsd: number;
  status: 'pending' | 'approved' | 'rejected';
  reason?: string;
  createdAt: string;
  decidedAt?: string;
  requesterName?: string;
  areaName?: string;
}

// Converte erro de validação da API (HTTP 400) em BudgetError, preservando a mensagem.
function asBudgetError(err: unknown): never {
  if (err instanceof ApiError && err.status === 400) {
    throw new BudgetError(err.message);
  }
  throw err;
}

export const budgetService = {
  async listAreas(): Promise<Area[]> {
    const res = await api.get<{ areas: Area[] }>('/budget/areas');
    return res.areas;
  },

  async listPeople(): Promise<Person[]> {
    const res = await api.get<{ people: Person[] }>('/budget/people');
    return res.people;
  },

  async saveArea(area: Area): Promise<Area> {
    try {
      const res = await api.post<{ area: Area }>('/budget/areas', area);
      return res.area;
    } catch (err) {
      asBudgetError(err);
    }
  },

  async removeArea(id: string): Promise<void> {
    try {
      await api.del(`/budget/areas/${encodeURIComponent(id)}`);
    } catch (err) {
      asBudgetError(err);
    }
  },

  async savePerson(person: Person): Promise<Person> {
    try {
      const res = await api.post<{ person: Person }>('/budget/people', person);
      return res.person;
    } catch (err) {
      asBudgetError(err);
    }
  },

  async removePerson(id: string): Promise<void> {
    try {
      await api.del(`/budget/people/${encodeURIComponent(id)}`);
    } catch (err) {
      asBudgetError(err);
    }
  },

  // --- Saldo extra (grants debitados da área) ---
  async requestGrant(amount: number, reason: string): Promise<void> {
    await api.post('/budget/grants', { amount, reason });
  },
  async listPendingGrants(): Promise<GrantRequest[]> {
    const res = await api.get<{ requests: GrantRequest[] }>('/budget/grants/pending');
    return res.requests;
  },
  async listMyGrants(): Promise<GrantRequest[]> {
    const res = await api.get<{ grants: GrantRequest[] }>('/budget/grants/mine');
    return res.grants;
  },
  async decideGrant(id: string, decision: 'approve' | 'reject'): Promise<void> {
    await api.post(`/budget/grants/${encodeURIComponent(id)}/decide`, { decision });
  },

  // Cálculo puro (client-side) — derivado das listas já carregadas.
  areaUsage(area: Area, allPeople: Person[]): AreaUsage {
    const allocated = allPeople
      .filter((p) => p.areaId === area.id)
      .reduce((acc, p) => acc + p.budgetBRL, 0);
    return { budget: area.budgetBRL, allocated, remaining: area.budgetBRL - allocated };
  },

  // Quanto do budget da pessoa já está alocado em agentes/fluxos (exclui uma entidade opcional).
  async personUsage(person: Person, excludeEntityId?: string): Promise<PersonUsage> {
    const [agents, flows] = await Promise.all([agentsService.list(), flowsService.list()]);
    let allocated = 0;
    agents.forEach((a) => {
      if (a.id !== excludeEntityId && a.costControl?.personId === person.id) {
        allocated += a.costControl.monthlyCapBRL || 0;
      }
    });
    flows.forEach((f) => {
      if (f.id !== excludeEntityId && f.costControl?.personId === person.id) {
        allocated += f.costControl.monthlyCapBRL || 0;
      }
    });
    return { budget: person.budgetBRL, allocated, remaining: person.budgetBRL - allocated };
  },
};
